<template>
  <div>
    <v-layout row wrap class="pt-4 pb-1">
      <!--provider name select-->
      <v-flex xs12 sm4 :class="{'pb-0': isMobile}">
        <v-autocomplete
          v-model="selectedProvider"
          dense
          label="Provider Name/Credentials"
          placeholder="Enter Provider Name"
          :items="providersArray"
          :clearable="true"
          :error-messages="providerSearchError"
          :return-object="true"
          :search-input.sync="providerSearchInput"
          item-text="username"
          :loading="providerSearchLoading"
          :no-filter="true"
          @focus="clearProviderAutoComplete">
          <!--this is how the data in the select appears-->
          <template slot="item" slot-scope="{item, tile}">
            {{item.firstName}} {{item.lastName}}
            <span v-if="isMobile && item.credentialType">/{{item.credentialType}}</span>
            <span v-if="!isMobile && item.credentialName">/{{item.credentialName}}</span>
          </template>
          <template slot="selection" slot-scope="{item, selected}">
            {{item.firstName}} {{item.lastName}}
            <span v-if="isMobile && item.credentialType">/{{item.credentialType}}</span>
            <span v-if="!isMobile && item.credentialName">/{{item.credentialName}}</span>
          </template>
          <!--message that displays when search matches nothing-->
          <template slot="no-data">
            <v-list>
              <!--message when no input is entered-->
              <v-list-tile v-if="!providerSearchInput">
                Please type a Provider.
              </v-list-tile>
              <!--message when no provider matches text-->
              <v-list-tile v-if="providerSearchInput && !providerSearchLoading">
                No Results
              </v-list-tile>
            </v-list>
          </template>
        </v-autocomplete>
      </v-flex>
      <!--school location combobox-->
      <v-flex xs12 sm4 :class="{'py-0': isMobile}">
        <v-autocomplete
          v-model="selectedSchool"
          dense
          label="Student's School"
          placeholder="Enter Student's School"
          :items="schoolArray"
          :clearable="true"
          :return-object="true"
          :search-input.sync="schoolSearchInput"
          item-text="schoolName"
          :error-messages="schoolSearchError"
          :loading="schoolSearchLoading"
          :no-filter="true"
          @focus="clearSchoolAutoComplete">
          <!--message that displays when search matches nothing-->
          <template slot="no-data">
            <v-list>
              <!--message when no input is entered-->
              <v-list-tile v-if="!schoolSearchInput">
                Please type a School.
              </v-list-tile>
              <!--message when no provider matches text-->
              <v-list-tile
                v-if="schoolSearchInput && !schoolSearchLoading">
                No Results
              </v-list-tile>
            </v-list>
          </template>
        </v-autocomplete>
      </v-flex>
      <!--retrieve students button-->
      <v-flex xs12 sm4 :class="{'pt-0': isMobile}">
        <v-btn
          class="retrieve-students-button"
          color="primary"
          :disabled="!selectedSchool || !selectedProvider"
          block
          @click="getStudentList">
          Retrieve Students
        </v-btn>
      </v-flex>
    </v-layout>
    <!--student/service container-->
    <v-toolbar class="student-service-toolbar" :class="$mq" color="primary" dark>
      <v-toolbar-title class="white--text">
          <span
            v-if="retrieveStudentsClicked"
            :class="{
              'subheading': !isMobile,
              'body-2 small': isMobile
            }">
            <span v-if="providerInformation" class="font-weight-bold">
              {{ providerInformation }}
              <span v-if="isMobile && providerCredentialType">
                {{ providerCredentialType }}
              </span>
              <span v-if="!isMobile && providerCredentialName">
                {{ providerCredentialName }}
              </span>
            </span>
            <br>
            <span v-if="schoolName">
              {{ schoolName }}
            </span>
          </span>
      </v-toolbar-title>
      <v-spacer></v-spacer>
      <div class="mr-3">
        <v-layout row wrap>
          <v-flex
            v-if="!isMobile"
            style="padding-top: 20px; font-size: 16px"
            class="white--text"
            :class="{'font-weight-bold': !allSupports}">
            Unassigned
          </v-flex>
          <v-tooltip :disabled="!showTooltip" top>
            <v-switch
              slot="activator"
              color="accent"
              style="padding-top: 20px;"
              v-model="allSupports">
              <template slot="label">
                  <span :class="{'accent--text': allSupports,
                                 'font-weight-bold': allSupports,
                                 small: isMobile}">
                    All Services
                  </span>
              </template>
            </v-switch>
            <span>Toggle to view All Services or only services where provider is not assigned</span>
          </v-tooltip>
        </v-layout>
      </div>
    </v-toolbar>
    <v-flex v-if="studentsList && studentsList.length">
      <div v-for="(student, i) in studentsList" :key="i" class="py-3 student-container">
        <v-layout class="studentInfo" ma-0 pa-0>
          <v-flex :class="{'pa-0 pl-3': isMobile, 'py-1 px-4': !isMobile}">
            <span :class="{'body-2 pt-1 display-block': isMobile,
            'subheading font-weight-bold': !isMobile}">
              <span :class="{'font-weight-bold': isMobile}">
                {{student.firstName}} {{student.lastName}}
              </span>
              <v-tooltip :disabled="!showTooltip" top>
                <span slot="activator">({{student.studentExtId}})</span>
                <span>
                  Student ID
                </span>
              </v-tooltip>
            </span>
            <span :class="{'caption': isMobile,'subheading': !isMobile}">
                  <v-tooltip :disabled="!showTooltip" top>
                    <span slot="activator" :class="{'pl-3': !isMobile}">
                      {{student.gender}}
                    </span>
                    <span>Gender</span>
                  </v-tooltip>
                  <span :class="{'pl-2': isMobile, 'pl-3': !isMobile}">
                    <strong>Gr: {{student.grade}}</strong>
                  </span>
                  <span :class="{'pl-2': isMobile, 'pl-3': !isMobile}">
                    {{student.schoolName}}
                  </span>
                  <v-tooltip :disabled="!showTooltip" top>
                    <span slot="activator">({{student.schoolCode}})</span>
                    <span>Student's School Code</span>
                  </v-tooltip>
                </span>
          </v-flex>
        </v-layout>
        <!--repeatable service section-->
        <div v-if="student.services.length">
          <div
            v-for="(service, index) in student.services"
            :key="index"
            class="student-service-row">
            <!--<v-layout row wrap style="min-height: 20px;">-->
            <v-layout row rap mx-4 my-1>
              <v-flex xs11 sm10 md9 pa-0>
                <div :class="{'warning--text': !service.active,
                        'caption mobile-padding': isMobile,
                        'body-2 py-1': !isMobile}"
                      class="student-service-row-text">
                  <!--service type-->
                  <v-tooltip :disabled="!showTooltip" top>
                    <span slot="activator" :class="{'pr-3': isMobile}">
                      {{service.planTypeName}}: {{service.serviceType}}
                    </span>
                    <span>Plan Type/Service</span>
                  </v-tooltip>
                  <!--divider-->
                  <span v-if="!isMobile">
                    <v-icon class="dot-icon" :class="{'warning--text': !service.active}">
                      mdi-circle-small
                    </v-icon>
                  </span>
                  <!--start/end date-->
                  <v-tooltip :disabled="!showTooltip" top>
                    <span slot="activator">
                      {{service.startDate | formatDate}} -
                      {{service.endDate | formatDate}}
                    </span>
                    <span>Projected Start/End Dates</span>
                  </v-tooltip>
                  <!--divider-->
                  <span v-if="!isMobile">
                    <v-icon class="dot-icon" :class="{'warning--text': !service.active}">
                      mdi-circle-small
                    </v-icon>
                  </span>
                  <span v-if="isMobile"><br></span>
                  <!--frequency-->
                  <v-tooltip :disabled="!showTooltip" top>
                    <span slot="activator" :class="{'pr-3': isMobile}">
                      {{service.frequency}}
                    </span>
                    <span>Service Frequency</span>
                  </v-tooltip>
                  <span v-if="service.esy">
                    <!--divider-->
                    <span v-if="!isMobile">
                      <v-icon class="dot-icon" :class="{'warning--text': !service.active}">
                        mdi-circle-small
                      </v-icon>
                    </span>
                    <v-tooltip :disabled="!showTooltip" top>
                      <span slot="activator">(ESY)</span>
                      <span>has ESY</span>
                    </v-tooltip>
                  </span>
                </div>
              </v-flex>
              <v-flex xs1 sm2 md3 pa-0 text-xs-right>
                <div
                  @click="addProvider(service.serviceId)"
                  v-if="!!service.addType">
                  <div v-if="isMobile">
                    <span v-if="service.addType === 'TEAM'">
                      <v-icon class="secondary--text">
                        mdi-lock-plus
                      </v-icon>
                    </span>
                    <span v-else>
                      <v-icon class="secondary--text">
                        mdi-account-plus
                      </v-icon>
                    </span>
                  </div>
                  <div v-else-if="isTablet">
                    <v-btn class="actionButton-tablet" color="secondary" small round>
                      <span v-if="service.addType === 'TEAM'">
                        <v-tooltip :disabled="!showTooltip" top>
                          <span slot="activator">
                            <small>Add Team</small>
                          </span>
                          <span>Add provider to student team</span>
                        </v-tooltip>
                      </span>
                      <span v-if="service.addType === 'PROVIDER'">
                        <v-tooltip :disabled="!showTooltip" top>
                          <span slot="activator">
                            <small>Add Provider</small>
                          </span>
                          <span>Add provider to student team and service</span>
                        </v-tooltip>
                      </span>
                    </v-btn>
                  </div>
                  <div v-else>
                    <v-btn class="actionButton" color="secondary" small round>
                      <span v-if="service.addType === 'TEAM'">
                        <v-tooltip :disabled="!showTooltip" top>
                          <span slot="activator">
                            <v-icon small class="icon-padding">
                              mdi-lock-plus
                            </v-icon>
                            <small>Add Team</small>
                          </span>
                          <span>Add provider to student team</span>
                        </v-tooltip>
                      </span>
                      <span v-if="service.addType === 'PROVIDER'">
                        <v-tooltip :disabled="!showTooltip" top>
                          <span slot="activator">
                            <v-icon small class="icon-padding">mdi-account-plus</v-icon>
                            <small>Add Provider</small>
                          </span>
                          <span>Add provider to student team and service</span>
                        </v-tooltip>
                      </span>
                    </v-btn>
                  </div>
                </div>
                <div
                  v-if="!!service.removeType"
                  @click="removeProvider(service.providerId)">
                  <div v-if="isMobile">
                    <span v-if="service.removeType === 'TEAM'">
                      <v-icon class="error--text">
                        mdi-lock-open
                      </v-icon>
                    </span>
                    <span v-if="service.removeType === 'PROVIDER'">
                      <v-icon class="error--text">
                        mdi-account-remove
                      </v-icon>
                    </span>
                  </div>
                  <div v-else-if="isTablet">
                    <v-btn class="actionButton-tablet remove" small round>
                      <span v-if="service.removeType === 'TEAM'">
                        <v-tooltip :disabled="!showTooltip" top>
                          <span slot="activator">
                            <small>Remove from Team</small>
                          </span>
                          <span>Remove provider from student team</span>
                        </v-tooltip>
                      </span>
                      <span v-if="service.removeType === 'PROVIDER'">
                        <v-tooltip :disabled="!showTooltip" top>
                          <span slot="activator">
                            <small>
                              Remove Provider
                            </small>
                          </span>
                          <span>Remove provider from student team and service</span>
                        </v-tooltip>
                      </span>
                    </v-btn>
                  </div>
                  <div v-else>
                    <v-btn class="actionButton remove" small round>
                      <span v-if="service.removeType === 'TEAM'">
                        <v-tooltip :disabled="!showTooltip" top>
                          <span slot="activator">
                            <v-icon small style="padding-right: 10px;">
                              mdi-lock-open
                            </v-icon>
                            <small>Remove From Team</small>
                          </span>
                          <span>Remove provider from student team</span>
                        </v-tooltip>
                      </span>
                      <span v-if="service.removeType === 'PROVIDER'">
                        <v-tooltip :disabled="!showTooltip" top>
                          <span slot="activator">
                            <v-icon small style="padding-right: 10px;">
                              mdi-account-remove
                            </v-icon>
                            <small>Remove Provider</small>
                          </span>
                          <span>Remove provider from student team and service</span>
                        </v-tooltip>
                      </span>
                    </v-btn>
                  </div>
                </div>
              </v-flex>
              <!--spacer-->
              <div v-if="index < student.services.length - 1"
                   :class="{'mt-50': isMobile}">
              </div>
            </v-layout>
            <v-alert :value="!!service.errorMessage" type="error">
              {{service.errorMessage}}
            </v-alert>
          </div>
        </div>
      </div>
    </v-flex>
    <v-flex v-if="!retrieveStudentsClicked">
      <v-flex class="font-italic py-3 px-1">
        No Students.  Select a Provider and Student's School above,
        then click on "Retrieve Students"
      </v-flex>
    </v-flex>
    <v-alert
      class="mt-3 mb-5"
      :value="errorMsg"
      type="error">
      {{errorMsg}}
    </v-alert>
    <full-screen-loading-spinner v-if="showLoading"></full-screen-loading-spinner>
  </div>
</template>

<script>
import api from '@/utils/api';
import FullScreenLoadingSpinner from '@/components/full-screen-loading-spinner';
import * as Constants from '@/constants';

export default {
  name: 'ProviderAdd',
  components: {
    FullScreenLoadingSpinner
  },
  props: ['showTooltip'],
  data() {
    return {
      selectedProvider: null,
      selectedProviderCopy: null,
      selectedSchool: null,
      selectedSchoolCopy: null,
      errorMsg: null,
      studentsList: null,
      allSupports: false,
      // provider autocomplete
      providerSearchError: '',
      providerSearchInput: null,
      providerSearchLoading: false,
      providersArray: [],
      // school autocomplete
      schoolSearchError: '',
      schoolSearchInput: null,
      schoolSearchLoading: false,
      schoolArray: [],
      // app loading screen
      showLoading: false,
      retrieveStudentsClicked: false,
      providerInformation: '',
      providerCredentialType: '',
      providerCredentialName: '',
      schoolName: ''
    };
  },
  computed: {
    isMobile() {
      return this.$mq === 'mobile';
    },
    isTablet() {
      return this.$mq === 'tablet';
    },
    isDesktop() {
      return this.$mq === 'laptop' || this.$mq === 'desktop';
    }
  },
  methods: {
    clearProviderAutoComplete() {
      if (!this.selectedProvider) {
        this.providersArray = [];
      }
    },
    clearSchoolAutoComplete() {
      if (!this.selectedSchool) {
        this.schoolArray = [];
      }
    },
    getStudentList() {
      this.errorMsg = null;
      this.showLoading = true;
      this.studentsList = null;
      this.retrieveStudentsClicked = true;
      api.get(Constants.GET_STUDENT_SERVICE_LIST_BY_PROVIDER_URL(this.selectedProviderCopy.userId, this.selectedSchoolCopy.schoolId, !this.allSupports))
        .then((res) => {
          if (!res.data.data.length) throw new Error('No students found');
          this.studentsList = res.data.data;
        })
        .catch((error) => {
          this.errorMsg = error.message;
        })
        .finally(() => {
          // set information for title bar
          this.providerInformation = `${this.selectedProviderCopy.firstName} ${this.selectedProviderCopy.lastName}`;
          this.providerCredentialType =
            this.selectedProviderCopy.credentialType ? `/${this.selectedProviderCopy.credentialType}` : null;
          this.providerCredentialName =
            this.selectedProviderCopy.credentialName ? `/${this.selectedProviderCopy.credentialName}` : null;
          this.schoolName = this.selectedSchoolCopy.schoolName;
          // clear form fields
          this.selectedProvider = null;
          this.selectedSchool = null;
          this.showLoading = false;
        });
    },
    addProvider(id) {
      const body = {
        serviceId: id,
        providerUserId: this.selectedProviderCopy.userId
      };
      this.errorMsg = null;
      this.showLoading = true;
      api.post(Constants.ADD_PROVIDER_TO_STUDENT_SERVICE_AND_TEAM_URL(), body)
        .then(() => {
          this.getStudentList();
        })
        .catch((error) => {
          this.errorMsg = error.message;
          this.showLoading = false;
        });
    },
    removeProvider(providerId) {
      this.errorMsg = null;
      this.showLoading = true;
      api.delete(Constants.REMOVE_PROVIDER_FROM_STUDENT_SERVICE_AND_TEAM_URL(providerId))
        .then(() => {
          this.getStudentList(true);
        })
        .catch((error) => {
          this.errorMsg = error.message;
          this.showLoading = false;
        });
    }
  },
  watch: {
    // fires when user toggles the all services switch
    allSupports() {
      // make sure theres a selected provider and selected school before toggling
      if (this.selectedProviderCopy && this.selectedSchoolCopy) this.getStudentList();
    },
    // fires the api call to get providers list
    providerSearchInput() {
      // show loading
      this.providerSearchLoading = true;
      // clear timeout if function is called before setTimeout fires
      clearTimeout(this.timeout);
      // make sure the user types something before firing request
      if (this.providerSearchInput.length >= 2) {
        // fire the request after a delay
        this.timeout = setTimeout(() => {
          api.get(Constants.GET_SCHEDULER_PROVIDER_LIST_URL(this.providerSearchInput))
            .then((res) => {
              this.providersArray = res.data.data;
            })
            .catch((error) => {
              this.providerSearchError = error.message;
            })
            .finally(() => {
              // stop loading
              this.providerSearchLoading = false;
            });
        }, 300);
      } else {
        this.providerSearchLoading = false;
      }
    },
    // fires the api call to get school list
    schoolSearchInput() {
      // show loading
      this.schoolSearchLoading = true;
      // clear timeout if function is called before setTimeout fires
      clearTimeout(this.timeout);
      // make sure the user types something before firing request
      if (this.schoolSearchInput) {
        // fire the request after a delay
        this.timeout = setTimeout(() => {
          api.get(Constants.GET_SCHEDULER_SCHOOL_LIST_URL(this.schoolSearchInput))
            .then((res) => {
              this.schoolArray = res.data.data;
            })
            .catch((error) => {
              this.schoolSearchError = error.message;
            })
            .finally(() => {
              this.schoolSearchLoading = false;
            });
        }, 300);
      } else {
        this.schoolSearchLoading = false;
      }
    },
    selectedProvider(newVal, oldVal) {
      if (newVal) {
        this.selectedProviderCopy = Object.assign({}, newVal);
      }
      if (!newVal && oldVal) this.providersArray = [];
    },
    selectedSchool(newVal, oldVal) {
      if (newVal) {
        this.selectedSchoolCopy = Object.assign({}, newVal);
      }
      if (!newVal && oldVal) this.schoolArray = [];
    }
  }
};
</script>

<style lang="scss" scoped>
  .retrieve-students-button {
    height: 47px;
    border-radius: 5px;
    box-shadow: 0 0 10px rgba(0, 0, 0, 0.1) !important;
  }
  .student-service-toolbar {
    height: 64px;
    border-radius: 10px;
    .v-toolbar__title {
      line-height: 1.3;
    }
    /deep/ .v-toolbar__content {
      height: 100% !important;
    }
    &.mobile {
      .v-toolbar__title {
        line-height: 1;
      }
    }
  }
  .student-container {
    margin: 24px 0;
    box-shadow: 0 0 8px rgba(0, 0, 0, 0.2) !important;
    border-radius: 10px;
    .student-service-row {
      height: 100%;
      &:hover {
        background: rgba(225, 225, 225, 0.25);
      }
      .student-service-row-text {
        margin-top: 5px;
      }
      .dot-icon {
        vertical-align: middle;
      }
    }
  }
  .icon-padding {
    padding-right: 10px;
  }

  .actionButton {
    width: 145px;
    &.remove {
      color: var(--v-error-base);
      box-shadow: 0 0 8px rgba(0, 0, 0, 0.12);
    }
  }

  .actionButton-tablet {
    width: 105px;
    &.remove {
      color: var(--v-error-base);
      box-shadow: 0 0 8px rgba(0, 0, 0, 0.12);
    }
  }

  .studentInfo {
    font-size: 1.2em;
    color: var(--v-primary-base);
  }
</style>



// WEBPACK FOOTER //
// src/components/scheduler/add-by-provider.vue